import type { ValueEvent } from '@adornis/base/utilTypes';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar';
import { XNativeDialog } from '@adornis/dialog/x-native-dialog';
import { useState } from '@adornis/functional-lit/stateful.js';
import { webcomponent } from '@adornis/functional-lit/webcomponent';
import { html } from 'lit';
import { DateTime } from 'luxon';
import { firstValueFrom, timer } from 'rxjs';
import { publishDraft } from '../api/publishDraft';
import type { Draft } from '../db/Draft';

type PublishDraftDialogResult = {
  publish: boolean;
  scheduled: boolean;
  scheduledAt?: Date;
};

const PublishDraftDialogContent = webcomponent(
  'publish-draft-dialog',
  (props: { resolve: (value: PublishDraftDialogResult) => void; reject: () => void }) => {
    const { resolve, reject } = props;

    const [state, setState] = useState({
      scheduledDate: '',
      scheduledTime: '',
      isScheduling: false,
      publish: false,
    });

    const handlePublishNow = () => {
      resolve({ publish: state.publish, scheduled: false });
    };

    const handleScheduledPublish = () => {
      if (!state.scheduledDate || !state.scheduledTime) {
        return;
      }

      const scheduledDateTime = new Date(`${state.scheduledDate}T${state.scheduledTime}`);
      resolve({
        publish: true,
        scheduled: true,
        scheduledAt: scheduledDateTime,
      });
    };

    return html`
      <div class="card w-96 bg-base-100 shadow-xl">
        <div class="card-body">
          <h2 class="card-title text-center">Entwurf auf Originalseite übertrage</h2>

          <d-hr></d-hr>

          <d-checkbox
            .value=${state.publish}
            @value-picked=${(e: ValueEvent<boolean>) => setState({ ...state, publish: e.detail.value })}
            .label=${'Live schalten'}
          >
          </d-checkbox>

          <d-checkbox
            .value=${state.isScheduling}
            @value-picked=${(e: ValueEvent<boolean>) => setState({ ...state, isScheduling: e.detail.value })}
            .label=${'Übertragung planen'}
          >
          </d-checkbox>

          ${state.isScheduling
            ? html`
                <div class="grid grid-cols-2 gap-4 mt-2">
                  <div class="form-control">
                    <label class="label">
                      <span class="label-text">Datum</span>
                    </label>
                    <input
                      type="date"
                      class="input input-bordered"
                      .value=${state.scheduledDate}
                      @input=${e => setState({ ...state, scheduledDate: e.target.value })}
                    />
                  </div>
                  <div class="form-control">
                    <label class="label">
                      <span class="label-text">Uhrzeit</span>
                    </label>
                    <input
                      type="time"
                      class="input input-bordered"
                      .value=${state.scheduledTime}
                      @input=${e => setState({ ...state, scheduledTime: e.target.value })}
                    />
                  </div>
                </div>
              `
            : ''}

          <d-button
            @click=${() => {
              if (state.isScheduling) {
                handleScheduledPublish();
              } else {
                handlePublishNow();
              }
            }}
            >${state.isScheduling ? 'Ausführen' : 'Jetzt veröffentlichen'}</d-button
          >

          <div class="card-actions justify-start mt-4">
            <button class="btn btn-ghost" @click=${() => reject()}>Abbrechen</button>
          </div>
        </div>
      </div>
    `;
  },
  {
    shadowRoot: true,
  },
);

export const PublishDraftDialog = async (draft: Draft) => {
  return XNativeDialog.showPopup({
    content(resolve: (value: PublishDraftDialogResult) => void, reject, dialogElement) {
      return html` ${PublishDraftDialogContent.render({ resolve, reject })} `;
    },
  }).then(result => {
    if (!result) return;

    XNativeDialog.waitFor(
      Promise.all([
        publishDraft(
          draft._id,
          result.publish,
          result.scheduledAt ? DateTime.fromJSDate(result.scheduledAt) : undefined,
        )(),
        firstValueFrom(timer(1000)),
      ]),
    ).then(() => {
      XSnackbar.show('Entwurf veröffentlicht!');
    });
  });
};
